<template>
  <div v-if="pageReady" class="home">
    <!-- ====== 1. Hero ====== -->
    <section class="home-hero home__section" data-section="hero">
      <video
        class="home-hero__video"
        :src="require('@/assets/images/home/ad.webm')"
        autoplay
        loop
        muted
        playsinline
      ></video>
      <div class="home-hero__overlay"></div>
      <div class="home-hero__content">
        <p class="home-hero__tag">{{ $t('快速搭建你的在线商店') }}</p>
        <h1 class="home-hero__title">{{ $t('商品跨境开店') }}<br>{{ $t('从独立站开始掌控全球生意') }}</h1>
        <ul class="home-hero__features">
          <li>{{ $t('零代码开店') }}&nbsp;·&nbsp;{{ $t('多语言商城') }}&nbsp;·&nbsp;{{ $t('一键铺货') }}&nbsp;·&nbsp;{{ $t('全球收付款') }}</li>
          <li class="home-hero__features-desc">{{ $t('10 分钟搭建品牌独立站，一键上架 商品货源，全球自动发货，轻松开启跨境电商生意') }}</li>
        </ul>
        <div class="home-hero__actions">
          <button class="home-hero__btn home-hero__btn--primary" @click="onCTA">{{ $t('立即免费开通') }}</button>

        </div>
        <p class="home-hero__note">{{ $t('无需技术背景') }}&nbsp;·&nbsp;{{ $t('不限办公地点') }}&nbsp;·&nbsp;{{ $t('即刻上架您的产品') }}</p>
      </div>
    </section>

    <!-- ====== 2. Pain Points ====== -->
    <section class="home-painpoints home__section" data-section="painpoints">
      <div class="home-painpoints__header">
        <span class="home-painpoints__label">{{ $t('行业痛点') }}</span>
        <h2 class="home-painpoints__title">{{ $t('为什么越来越多卖家选择独立站？') }}</h2>
      </div>
      <div class="home-painpoints__cards">
        <article v-for="(item, i) in painPoints" :key="i" class="home-painpoints__card">
          <img :src="item.icon" :alt="$t(item.problem)" class="home-painpoints__card-icon" loading="lazy">
          <div class="home-painpoints__card-body">
            <h3 class="home-painpoints__card-title">{{ $t(item.problem) }}</h3>
            <p class="home-painpoints__card-desc">{{ $t(item.solution) }}</p>
          </div>
        </article>
      </div>
      <p class="home-painpoints__quote">
        <img :src="img('flag')" alt="" class="home-painpoints__quote-icon" loading="lazy">
        <span>{{ $t('独立站 = 自主品牌 + 自由定价 + 客户沉淀 + 长期复利') }}</span>
      </p>
    </section>

    <!-- ====== 3. Site Builder ====== -->
    <!-- TODO: scroll-driven card stack animation needs more work -->
    <!--
    <section ref="builderSection" class="home-builder" :style="{ height: builderSectionHeight + 'px' }">
      <div class="home-builder__sticky">
        <div
          v-for="(item, idx) in builderCards"
          :key="idx"
          class="home-builder__card"
          :style="getBuilderCardStyle(idx)"
        >
          <div class="home-builder__card-inner">
            <div class="home-builder__card-text">
              <h2 class="home-builder__card-title">{{ $t(item.title) }}</h2>
              <p class="home-builder__card-subtitle">{{ $t(item.subtitle) }}</p>
              <p class="home-builder__card-desc">{{ $t(item.desc) }}</p>
              <div class="home-builder__card-action">
                <span>{{ $t(item.action) }}</span>
                <img :src="img('arrow')" alt="" loading="lazy">
              </div>
            </div>
            <div class="home-builder__card-visual">
              <img v-for="(imgSrc, vi) in item.images" :key="vi" :src="imgSrc" alt="" loading="lazy">
            </div>
          </div>
        </div>
      </div>
    </section>
    -->

    <!-- ====== 4. Full Chain ====== -->
    <section class="home-chain home__section" data-section="chain">
      <span class="home-chain__label">{{ $t('全链路闭环') }}</span>
      <h2 class="home-chain__title">{{ $t('建站、铺货、收款、物流，一个平台全部搞定') }}</h2>
      <p class="home-chain__desc">{{ $t('Taoify提供跨境电商全链路解决方案，告别多平台对接的烦恼') }}</p>
      <div class="home-chain__steps-wrap">
        <div class="home-chain__steps">
          <template v-for="(step, i) in chainSteps">
            <div :key="'s-' + i" class="home-chain__step">
              <img :src="step.icon" :alt="$t(step.title)" class="home-chain__step-icon" loading="lazy">
              <h3 class="home-chain__step-title">{{ $t(step.title) }}</h3>
              <p class="home-chain__step-desc">{{ $t(step.desc) }}</p>
            </div>
            <img v-if="i < chainSteps.length - 1" :src="chainArrows[i]" alt="" class="home-chain__arrow" loading="lazy">
          </template>
        </div>
      </div>
    </section>

    <!-- ====== 5. Core Comparison ====== -->
    <section class="home-compare home__section" data-section="compare">
      <span class="home-compare__label">{{ $t('核心对比') }}</span>
      <h2 class="home-compare__title">{{ $t('Taoify vs Shopify — 为什么越来越多的中国卖家选择Taoify') }}</h2>
      <div class="home-compare__table">
        <!-- left: Shopify column -->
        <div class="home-compare__left">
          <div class="home-compare__left-logo">
            <img :src="img('shopify')" alt="Shopify" loading="lazy">
          </div>
          <div v-for="(row, i) in comparisonRows" :key="'sl-' + i" class="home-compare__left-row">
            <img :src="require('@/assets/images/home/img-nocheck.png')" alt="" class="home-compare__left-icon" loading="lazy">
            <span>{{ $t(row.shopify) }}</span>
          </div>
        </div>
        <!-- middle: PK labels -->
        <div class="home-compare__pk">
          <div class="home-compare__pk-badge-wrap">
            <span class="home-compare__pk-text"></span>
          </div>
          <span v-for="(row, i) in comparisonRows" :key="'pk-' + i" class="home-compare__pk-label">{{ $t(row.label) }}</span>
        </div>
        <!-- right: Taoify advantages -->
        <div class="home-compare__right">
          <div class="home-compare__right-header">
            <img :src="img('taoifyCompare')" :alt="$t('Taoify优势')" class="home-compare__right-logo" loading="lazy">
          </div>
          <div v-for="(item, i) in taoifyAdvantages" :key="'ta-' + i" class="home-compare__right-row">
            <img :src="require('@/assets/images/home/img-check.png')" alt="" class="home-compare__right-icon" loading="lazy">
            <span>{{ $t(item) }}</span>
          </div>
        </div>
      </div>

      <!-- 移动端：行级双列对比卡片 -->
      <div class="home-compare__mobile">
        <div class="home-compare__mobile-head">
          <div class="home-compare__mobile-head--left">
            <img :src="img('shopify')" alt="Shopify" loading="lazy">
          </div>
          <div class="home-compare__mobile-vs-badge">VS</div>
          <div class="home-compare__mobile-head--right">
            <img :src="img('taoifyCompare')" alt="Taoify" loading="lazy">
          </div>
        </div>
        <div v-for="(row, i) in comparisonRows" :key="'m-'+i" class="home-compare__mobile-row">
          <span class="home-compare__mobile-label">{{ $t(row.label) }}</span>
          <div class="home-compare__mobile-vs">
            <div class="home-compare__mobile-cell home-compare__mobile-cell--left">
              <img :src="require('@/assets/images/home/img-nocheck.png')" alt="" class="home-compare__mobile-icon" loading="lazy">
              <span>{{ $t(row.shopify) }}</span>
            </div>
            <div class="home-compare__mobile-cell home-compare__mobile-cell--right">
              <img :src="require('@/assets/images/home/img-check.png')" alt="" class="home-compare__mobile-icon" loading="lazy">
              <span>{{ $t(taoifyAdvantages[i]) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
、

    <!-- ====== 7. Sell Anywhere ====== -->
    <section class="home-sell home__section" data-section="sell">
      <div class="home-sell__inner">
        <div class="home-sell__header">
          <div>
            <h2 class="home-sell__title">
              {{ $t('在任何时间地点') }}<span class="home-sell__title--accent">{{ $t('销售') }}</span>{{ $t('您的产品') }}
            </h2>
            <p class="home-sell__desc">{{ $t('随时随地不受地域时间限制，轻松售卖自有产品，无需线下门店束缚，全域触达客户，经营更灵活、变现更高效。') }}</p>
          </div>
          <button class="home-sell__btn" @click="onCTA">
            <span>{{ $t('开始销售') }}</span>
            <img :src="img('arrow')" alt="" loading="lazy">
          </button>
        </div>
        <div class="home-sell__cards">
          <article
            v-for="(card, i) in sellCards"
            :key="i"
            class="home-sell__card"
            :class="{ 'home-sell__card--reverse': i === 1 }"
          >
            <img :src="card.image" :alt="$t(card.title)" class="home-sell__card-img" loading="lazy">
            <div class="home-sell__card-row">
              <div class="home-sell__card-body">
                <h3 class="home-sell__card-title">{{ $t(card.title) }}</h3>
                <p class="home-sell__card-desc">{{ $t(card.desc) }}</p>
              </div>
              <img :src="require('@/assets/images/home/SellAnywhere-arrow.png')" alt="" class="home-sell__card-arrow" loading="lazy">
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ====== 8. Marketing ====== -->
    <section class="home-marketing home__section" data-section="marketing">
      <div class="home-marketing__inner">
        <h2 class="home-marketing__title">{{ $t('全渠道营销能力，助力品牌高效获客') }}</h2>
        <div class="home-marketing__tabs">
          <button
            v-for="(tab, i) in marketingTabs"
            :key="i"
            :class="['home-marketing__tab', { 'home-marketing__tab--active': activeTab === i }]"
            @click="activeTab = i"
          >{{ $t(tab) }}</button>
        </div>
        <div class="home-marketing__content">
          <!-- Tab 1: 社媒推广 -->
          <div v-show="activeTab === 0" class="home-marketing__panel">
            <div class="home-marketing__info">
              <h3 class="home-marketing__info-title">{{ $t('全域社媒分发') }}</h3>
              <p class="home-marketing__info-subtitle">{{ $t('多平台一键同步，拓宽曝光渠道') }}</p>
              <p class="home-marketing__info-desc">{{ $t('一键同步商品目录至 Facebook、Instagram、TikTok、Snapchat 等平台，精准触达目标受众') }}</p>
              <p class="home-marketing__info-desc">{{ $t('支持商品一键分享至社媒，轻松提升曝光与转化机会') }}</p>
            </div>
            <div class="home-marketing__visuals">
              <img :src="require('@/assets/images/home/Marketing-1.png')" alt="Social Media" loading="lazy">
            </div>
          </div>
          <!-- Tab 2: 广告追踪 -->
          <div v-show="activeTab === 1" class="home-marketing__panel">
            <div class="home-marketing__info">
              <h3 class="home-marketing__info-title">{{ $t('广告数据追踪') }}</h3>
              <p class="home-marketing__info-subtitle">{{ $t('免开发部署，精准监测广告效果') }}</p>
              <p class="home-marketing__info-desc">{{ $t('支持 Pinterest、Twitter、Snapchat、Kwai 等平台绑定，全面追踪投放效果') }}</p>
              <p class="home-marketing__info-desc">{{ $t('0 代码快速安装，全用户行为事件上报，助力优化广告投放策略') }}</p>
            </div>
            <div class="home-marketing__visuals">
              <img :src="require('@/assets/images/home/Marketing-2.png')" :alt="$t('广告追踪')" loading="lazy">
            </div>
          </div>
          <!-- Tab 3: 数据复盘 -->
          <div v-show="activeTab === 2" class="home-marketing__panel">
            <div class="home-marketing__info">
              <h3 class="home-marketing__info-title">{{ $t('整合数据洞察') }}</h3>
              <p class="home-marketing__info-subtitle">{{ $t('多维数据汇总，科学指导营销决策') }}</p>
              <p class="home-marketing__info-desc">{{ $t('跨社媒渠道推广销售，自动同步订单与库存数据，告别信息孤岛') }}</p>
              <p class="home-marketing__info-desc">{{ $t('按渠道、地区、商品维度，清晰展示流量与销售数据，快速优化广告与运营策略') }}</p>
            </div>
            <div class="home-marketing__visuals">
              <img :src="require('@/assets/images/home/Marketing-3.png')" :alt="$t('数据复盘')" loading="lazy">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== 9. Trust Metrics ====== -->
    <section class="home-trust home__section" data-section="trust">
      <h2 class="home-trust__title">{{ $t('超百万卖家选择Taoify创建跨境电商独立站') }}</h2>
      <div class="home-trust__stats">
        <template v-for="(stat, i) in trustStats">
          <div :key="'s-' + i" class="home-trust__stat">
            <div class="home-trust__stat-value">
              <span class="home-trust__stat-num">{{ stat.num }}</span>
              <span v-if="stat.unit" class="home-trust__stat-unit" :class="{ 'is-plus': stat.isPlus }">{{ stat.unit }}</span>
            </div>
            <span class="home-trust__stat-label">{{ $t(stat.label) }}</span>
          </div>
          <span v-if="i < trustStats.length - 1" :key="'d-' + i" class="home-trust__dot" :class="'home-trust__dot--' + dotColors[i]"></span>
        </template>
      </div>
    </section>

    <!-- ====== 10. Target Audience ====== -->
    <section class="home-audience home__section" data-section="audience">
      <div class="home-audience__header">
        <span class="home-audience__label">{{ $t('适用人群') }}</span>
        <h2 class="home-audience__title">{{ $t('无论你是谁，都能用Taoify轻松出海') }}</h2>
      </div>
      <div class="home-audience__cards">
        <article v-for="(p, i) in personas" :key="i" class="home-audience__card">
          <img :src="p.avatar" :alt="$t(p.title)" class="home-audience__card-avatar" loading="lazy">
          <h3 class="home-audience__card-title">{{ $t(p.title) }}</h3>
          <p class="home-audience__card-subtitle">{{ $t(p.subtitle) }}</p>
          <span class="home-audience__card-feature">{{ $t(p.feature) }}</span>
        </article>
      </div>
    </section>

    <!-- ====== 11. CTA ====== -->
    <section class="home-cta home__section" data-section="cta">
      <div class="home-cta__inner">
        <h2 class="home-cta__title">{{ $t('准备好开始你的跨境电商之旅了吗？') }}</h2>
        <p class="home-cta__desc">{{ $t('零代码、零佣金、全链路闭环，10 分钟拥有你的品牌独立站') }}</p>
        <button class="home-cta__btn" @click="onCTA">{{ $t('立即免费开通') }}</button>
      </div>
    </section>

  </div>
</template>

<script>
const CDN = 'https://lanhu-oss-2537-2.lanhuapp.com'

export default {
  name: 'Home',

  data () {
    return {
      pageReady: true,
      email: '',
      activeTab: 0,

      painPoints: [
        {
          icon: require('@/assets/images/home/PainPoints-1@2x.png'),
          problem: '店铺说封就封，客户资产一夜归零',
          solution: '→ 独立站自主掌控，客户资产永不丢失'
        },
        {
          icon: require('@/assets/images/home/PainPoints-2@2x.png'),
          problem: '平台抽成高达 15%，利润被不断压缩',
          solution: '→ Taoify 零平台佣金，利润100%归你'
        },
        {
          icon: require('@/assets/images/home/PainPoints-3@2x.png'),
          problem: '流量越来越贵，转化越来越难',
          solution: '→ 私域运营 + SEO，打造长期流量池'
        }
      ],

      chainSteps: [
        { title: '快速建站', desc: '10分钟，零代码，拥有专业品牌独立站', icon: require('@/assets/images/home/FullChain-1@2x.png') },
        { title: '海量铺货', desc: '1688/淘宝等商品一键采集，AI翻译，批量上架', icon: require('@/assets/images/home/FullChain-2@2x.png') },
        { title: '便捷收款', desc: '支持全球主流支付方式，资金安全直达', icon: require('@/assets/images/home/FullChain-3@2x.png') },
        { title: '智能物流', desc: '多仓发货，云仓模式，实时跟踪，全球送达', icon: require('@/assets/images/home/FullChain-4@2x.png') },
        { title: '持续运营', desc: '数据分析，SEO优化，会员管理，后续跟踪服务', icon: require('@/assets/images/home/FullChain-5@2x.png') }
      ],

      chainArrows: [
        require('@/assets/images/home/FullChain-arrow@2x.png'),
        require('@/assets/images/home/FullChain-arrow@2x.png'),
        require('@/assets/images/home/FullChain-arrow@2x.png'),
        require('@/assets/images/home/FullChain-arrow@2x.png')
      ],

      comparisonRows: [
        { label: '多语言支持', shopify: '需插件支持' },
        { label: '月费', shopify: '$29/月起' },
        { label: '交易佣金', shopify: '0.5%-2%' },
        { label: '国内收款', shopify: '配置复杂' },
        { label: '多平台铺货', shopify: '需付费插件' },
        { label: '中文客服', shopify: '时差响应慢' }
      ],

      sellCards: [
        {
          image: require('@/assets/images/home/SellAnywhere-1.png'),
          title: '海量商品，一键上架',
          desc: '一键采集 + AI翻译 + 批量上架，快速铺货到您的独立站',
          tag: '免费套餐+一键铺货+代发货'
        },
        {
          image: require('@/assets/images/home/SellAnywhere-2.png'),
          title: '全球物流，自动履约',
          desc: '支持 一件代发、质检、定制包装、品牌加工 等 100+ 服务，订单自动同步、物流轨迹实时更新',
          tag: '多仓发货+实时跟踪'
        },
        {
          image: require('@/assets/images/home/SellAnywhere-3.png'),
          title: '全链路闭环，省心运营',
          desc: '从建站 → 铺货 → 收款 → 发货 → 售后，一站式完成，专属客服 + CEO 邮箱通道，问题直达高层，快速响应',
          tag: '专属客服+CEO通道'
        }
      ],

      marketingTabs: ['社媒推广', '广告追踪', '数据复盘'],

      trustStats: [
        { num: '10', unit: 'W+', label: '注册卖家', isPlus: false },
        { num: '100', unit: '', label: '覆盖国家', isPlus: false },
        { num: '95%', unit: '+', label: '满意度', isPlus: true },
        { num: '1000', unit: 'W+', label: '经济活动量', isPlus: false }
      ],

      dotColors: ['pink', 'green', 'purple'],

      personas: [
        { avatar: require('@/assets/images/home/TargetAudience-1.png'), title: '个人卖家', subtitle: '无技术、无货源、预算有限', feature: '免费套餐+一键铺货+代发货' },
        { avatar: require('@/assets/images/home/TargetAudience-2.png'), title: '工厂老板', subtitle: '想绕过中间商直接出海', feature: 'B2B展示+询盘系统+品牌站' },
        { avatar: require('@/assets/images/home/TargetAudience-3.png'), title: '贸易公司', subtitle: '多品牌、多店铺管理', feature: '批量建站+统一后台+API对接' },
        { avatar: require('@/assets/images/home/TargetAudience-4.png'), title: '品牌卖家', subtitle: '品牌溢价+私域运营', feature: '专业模板+SEO+会员系统' }
      ],

      taoifyAdvantages: [
        '支持20+语言', '完全免费', '0佣金',
        '支付宝/微信/连连', '1688/淘宝等平台一键铺货', '7*24小时支持'
      ]
    }
  },

  mounted () {
    this.$nextTick(() => { this.initIntersectionObserver() })
  },

  beforeDestroy () {
    if (this._ioObserver) { this._ioObserver.disconnect() }
  },

  methods: {
    img (key) {
      const map = {
        play: require('@/assets/images/home/img-play.png'),
        flag: require('@/assets/images/home/img-flag.png'),
        shopify: require('@/assets/images/home/CoreComparison-shopify@2x.png'),
        taoifyCompare: require('@/assets/images/home/CoreComparison-taoify@2x.png'),
        zeroArrow: CDN + '/FigmaDDSSlicePNG909fe093fd32f8900d64482969146de9.png',
        arrow: require('@/assets/images/home/right.png'),
        templates: CDN + '/FigmaDDSSlicePNG2b1204be22cc38f27748ef83dfaf3f17.png',
        floatPay: CDN + '/FigmaDDSSlicePNG3717d91a7cdc8c45d83115ea0f8938ab.png',
        floatPayBadge: CDN + '/FigmaDDSSlicePNGe1c6aebefa703977918c7f5343aa9c15.png',
        floatLogi: CDN + '/FigmaDDSSlicePNGba6758c56b29015fb34ee4509172cbfc.png',
        shipBadge1: CDN + '/FigmaDDSSlicePNG7cf28722e6967b61280e7a825bd70fa8.png',
        shipBadge2: CDN + '/FigmaDDSSlicePNG144ad7f6d0158d5b8456da9d261d1e89.png',
        floatMkt: CDN + '/FigmaDDSSlicePNG9f328ddd64cc2e051239f4962fb53e9b.png',
        floatMktRight: CDN + '/FigmaDDSSlicePNG2a3cc814c921171f88cc69e7e16cc9e1.png',
        sidebarTop: CDN + '/FigmaDDSSlicePNG547abb472a24e6affbf3461e20172a00.png'
      }
      return map[key] || ''
    },

    onCTA () { this.$router.push('/sign-up') },
    onDemo () { /* TODO: demo modal */ },

    initIntersectionObserver () {
      const sections = this.$el.querySelectorAll('.home__section')
      if (!sections.length) return

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const section = entry.target
            const name = section.dataset.section
            section.classList.add('home__section--visible')

            if (name === 'trust') {
              this.animateCountUp()
            }

            observer.unobserve(section)
          }
        })
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -60px 0px'
      })

      sections.forEach(s => observer.observe(s))
      this._ioObserver = observer
    },

    animateCountUp () {
      const statEls = this.$el.querySelectorAll('.home-trust__stat')
      statEls.forEach((el, idx) => {
        const stat = this.trustStats[idx]
        if (!stat) return
        const numEl = el.querySelector('.home-trust__stat-num')
        if (!numEl) return
        const targetText = stat.num
        const targetNum = parseFloat(targetText)
        const suffix = targetText.replace(/[\d.]+/g, '')
        let current = 0
        const duration = 1200
        const start = performance.now()
        const tick = (now) => {
          const elapsed = now - start
          const progress = Math.min(elapsed / duration, 1)
          const eased = 1 - Math.pow(1 - progress, 3)
          const value = Math.round(eased * targetNum)
          numEl.textContent = value + suffix
          if (progress < 1) {
            requestAnimationFrame(tick)
          } else {
            numEl.textContent = targetText
          }
        }
        requestAnimationFrame(tick)
      })
    },

    onSubmitEmail () {
      if (!this.email || !this.email.includes('@')) {
        this.$Toast({ content: this.$t('请输入有效的邮箱地址'), type: 'error' })
        return
      }
      this.$router.push({ path: '/sign-up', query: { email: this.email } })
    }
  }
}
</script>

<style scoped lang="scss">
/* ====== Design Tokens (from original design, DO NOT MODIFY VALUES) ====== */
.home {
  --h-primary: #7331FF;
  --h-primary-dark: #675BE6;
  --h-primary-light-8: rgba(115, 49, 255, 0.08);
  --h-primary-light-20: rgba(115, 49, 255, 0.2);
  --h-accent: #A09FFF;
  --h-purple-tag: #6355FF;
  --h-white: #FFFFFF;
  --h-text-dark: #333333;
  --h-text-black: #000000;
  --h-text-gray: #999999;
  --h-text-navy: #0F2128;
  --h-bg-gray: #F2F2F2;
  --h-bg-light: #F7F7F7;
  --h-bg-floating-1: #EEECFF;
  --h-bg-floating-2: #E2DCFE;
  --h-dot-pink: #FAA9B3;
  --h-dot-green: #A9EAC8;
  --h-dot-purple: #D595E9;

  --h-fs-hero: 52px;
  --h-fs-h1: 32px;
  --h-fs-h2: 28px;
  --h-fs-h3: 24px;
  --h-fs-xl: 20px;
  --h-fs-lg: 18px;
  --h-fs-md: 16px;
  --h-fs-sm: 14px;
  --h-fs-stat: 48px;

  --h-radius-sm: 8px;
  --h-radius-md: 20px;
  --h-radius-lg: 24px;
  --h-radius-xl: 34px;
  --h-radius-full: 45px;
  --h-radius-round: 50%;

  --h-shadow-btn: 0px 4px 4px 0px rgba(103, 91, 230, 0.2);
  --h-shadow-outline: 0px 4px 18px 0px rgba(103, 91, 230, 0.1);
  --h-shadow-card: 0px 4px 24px 0px rgba(103, 91, 230, 0.15);

  --h-width: 1920px;
  --h-content: 1200px;
  --h-margin: 360px;
  --h-font: 'Source Han Sans CN', 'PingFang SC', 'Microsoft YaHei', sans-serif;

  font-family: var(--h-font);
  background: var(--h-white);
  max-width: var(--h-width);
  margin: 0 auto;
  overflow-x: hidden;

  * { box-sizing: border-box; }
}

/* ====== Scroll Animations ====== */
@keyframes home-fade-up {
  from { opacity: 0; transform: translateY(40px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes home-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes home-slide-left {
  from { opacity: 0; transform: translateX(-60px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes home-slide-right {
  from { opacity: 0; transform: translateX(60px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes home-scale-in {
  from { opacity: 0; transform: scale(0.6); }
  to   { opacity: 1; transform: scale(1); }
}

/* Section animation triggers */
.home-hero.home__section--visible .home-hero__content {
  animation: home-fade-up .8s ease forwards;
}

.home-painpoints.home__section--visible .home-painpoints__card {
  opacity: 0;
  animation: home-fade-up .6s ease forwards;
  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: .15s; }
  &:nth-child(3) { animation-delay: .3s; }
}

.home-chain.home__section--visible {
  .home-chain__step {
    opacity: 0;
    animation: home-fade-up .6s ease forwards;
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: .2s; }
    &:nth-child(3) { animation-delay: .4s; }
    &:nth-child(4) { animation-delay: .6s; }
    &:nth-child(5) { animation-delay: .8s; }
  }
  .home-chain__arrow {
    opacity: 0;
    animation: home-fade-in .4s ease forwards;
    &:nth-child(1) { animation-delay: .3s; }
    &:nth-child(2) { animation-delay: .5s; }
    &:nth-child(3) { animation-delay: .7s; }
    &:nth-child(4) { animation-delay: .9s; }
  }
}

.home-compare.home__section--visible {
  .home-compare__left  { animation: home-slide-left  .7s ease forwards; }
  .home-compare__right { animation: home-slide-right .7s ease forwards; }
  .home-compare__pk    { animation: home-fade-in     .5s ease .3s forwards; }
}

.home-sell.home__section--visible .home-sell__card {
  opacity: 0;
  animation: home-fade-up .6s ease forwards;
  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: .15s; }
  &:nth-child(3) { animation-delay: .3s; }
}

.home-marketing.home__section--visible .home-marketing__panel {
  animation: home-fade-up .5s ease forwards;
}

.home-trust.home__section--visible {
  .home-trust__stat {
    opacity: 0;
    animation: home-scale-in .5s ease forwards;
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: .15s; }
    &:nth-child(3) { animation-delay: .3s; }
    &:nth-child(4) { animation-delay: .45s; }
  }
  .home-trust__dot {
    opacity: 0;
    animation: home-fade-in .3s ease forwards;
    &:nth-child(1) { animation-delay: .2s; }
    &:nth-child(2) { animation-delay: .35s; }
    &:nth-child(3) { animation-delay: .5s; }
  }
}

.home-audience.home__section--visible .home-audience__card {
  opacity: 0;
  animation: home-fade-up .6s ease forwards;
  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: .12s; }
  &:nth-child(3) { animation-delay: .24s; }
  &:nth-child(4) { animation-delay: .36s; }
}

.home-cta.home__section--visible .home-cta__inner {
  animation: home-fade-up .7s ease forwards;
}

/* ====== 1. Hero ====== */
.home-hero {
  width: var(--h-width);
  height: 720px;
  margin-top: -50px;
  position: relative;
  overflow: hidden;

  &__video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.66);
    z-index: 1;
  }

  &__content {
    position: relative;
    z-index: 2;
    padding: 180px 0 0 var(--h-margin);
  }

  &__tag {
    color: var(--h-white);
    font-size: var(--h-fs-md);
    margin: 0;
  }

  &__title {
    color: var(--h-white);
    font-size: var(--h-fs-hero);
    font-weight: 700;
    line-height: 1.5;
    margin: 15px 0 0;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 5px 0 0;
    color: var(--h-accent);
    font-size: var(--h-fs-h2);
    font-weight: 700;
    letter-spacing: 1px;
    line-height: 1.5;
  }

  &__features-desc {
    color: var(--h-white);
    font-size: var(--h-fs-sm);
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1;
    margin-top: 15px;
    max-width: 526px;
  }

  &__actions {
    display: flex;
    gap: 20px;
    margin-top: 32px;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    font-family: var(--h-font);
    font-size: var(--h-fs-lg);
    font-weight: 500;
    transition: opacity .2s;
    &:active { opacity: .7; }

    &--primary {
      width: 180px;
      height: 51px;
      background: linear-gradient(360deg, #AA98FC 0%, #7331FF 100%);
      border-radius: var(--h-radius-xl);
      box-shadow: var(--h-shadow-btn);
      color: var(--h-white);
    }

    &--outline {
      width: 180px;
      height: 52px;
      background: var(--h-white);
      border-radius: var(--h-radius-xl);
      box-shadow: var(--h-shadow-outline);
      color: var(--h-text-navy);
      gap: 8px;
    }

    &-icon { width: 36px; height: 36px; }
  }

  &__note {
    color: var(--h-white);
    font-size: var(--h-fs-sm);
    margin: 12px 0 0;
  }
}

/* ====== 2. Pain Points ====== */
.home-painpoints {
  text-align: center;

  &__header { margin-top: 47px; }

  &__label {
    color: var(--h-primary);
    font-size: var(--h-fs-xl);
    font-weight: 500;
  }

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    margin: 10px 0 0;
  }

  &__cards {
    display: flex;
    justify-content: space-between;
    width: 1201px;
    margin: 32px auto 0;
  }

  &__card {
    background: #F7F7F7;
    border-radius: 12px;
    width: 387px;

    display: flex;
    align-items: flex-start;
    padding: 24px 20px;
  }

  &__card-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__card-body {
    margin-left: 4px;
  }

  &__card-title {
    color: var(--h-text-dark);
    font-size: 18px;
    font-weight: 500;
    margin: 0;
  }

  &__card-desc {
    color: var(--h-primary);
    font-size: 14px;
    font-weight: 500;
    margin: 12px 0 0;
  }

  &__quote {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin: 60px auto 120px;

    span {
      font-size: var(--h-fs-xl);
      font-weight: 500;
      color: var(--h-text-dark);
    }
  }

  &__quote-icon { width: auto; height: auto; }
}

/* ====== 3. Builder ====== */
.home-builder {
  position: relative;

  &__sticky {
    position: sticky;
    top: 160px;
    height: calc(100vh - 160px);
    overflow: hidden;
  }

  &__card {
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 80%;
    max-width: var(--h-content);
    height: 420px;
    border-radius: 20px;
    color: var(--h-text-dark);
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0px 8px 40px rgba(0, 0, 0, 0.12);

    &:nth-child(1) { z-index: 1; background: #FBE7F4; }
    &:nth-child(2) { z-index: 2; background: #EEEBFF; }
    &:nth-child(3) { z-index: 3; background: #E0D7FE; }
    &:nth-child(4) {
      z-index: 4;
      background: #AF96FA;

      .home-builder__card-subtitle,
      .home-builder__card-desc { color: #fff; }

      .home-builder__card-action {
        background: #fff;
        color: #7331FF;

        img { filter: invert(18%) sepia(92%) saturate(3695%) hue-rotate(255deg) brightness(101%) contrast(101%); }
      }
    }
  }

  &__card-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    padding: 60px 72px;
  }

  &__card-text {
    flex: 0 0 45%;
    text-align: left;
  }

  &__card-title {
    color: #7331FF;
    font-size: 14px;
    font-weight: 600;
    margin: 0;
  }

  &__card-subtitle {
    color: #333333;
    font-size: 32px;
    font-weight: 600;
    margin: 8px 0 0;
  }

  &__card-desc {
    color: #333333;
    font-size: 14px;
    line-height: 1.6;
    margin: 12px 0 0;
  }

  &__card-action {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 32px;
    font-size: var(--h-fs-lg);
    font-weight: 500;
    background: linear-gradient(0deg, #AA98FC 0%, #7331FF 100%);
    border-radius: 34px;
    color: #fff;
    padding: 12px 32px;

    img { width: 28px; height: 28px; filter: brightness(0) invert(1); }
  }

  &__card-visual {
    flex: 0 0 50%;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 280px;
      border-radius: 8px;
      object-fit: contain;
    }
  }
}

/* ====== 4. Full Chain ====== */
.home-chain {
  text-align: center;

  &__label {
    display: block;
    color: var(--h-primary);
    font-size: var(--h-fs-xl);
    font-weight: 500;
    margin-top: 100px;
  }

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    margin: 10px 0 0;
  }

  &__desc {
    color: var(--h-text-gray);
    font-size: var(--h-fs-md);
    margin: 10px 0 0;
  }

  &__steps-wrap {
    width: 100%;
    background: var(--h-bg-light);
    margin-top: 60px;
    padding: 40px 0;
  }

  &__steps {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: var(--h-content);
    margin: 0 auto;
  }

  &__step {
    width: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__step-icon { width: 80px; height: auto; }

  &__step-title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-xl);
    font-weight: 600;
    margin: 16px 0 0;
  }

  &__step-desc {
    color: var(--h-text-dark);
    font-size: var(--h-fs-sm);
    margin: 8px 0 0;
  }

  &__arrow {
    width: 43px;
    height: auto;
    align-self: center;
    margin-top: -90px;
  }
}

/* ====== 5. Core Comparison ====== */
.home-compare {
  width: var(--h-content);
  margin: 15px auto 0;

  &__label {
    display: block;
    text-align: center;
    color: var(--h-primary);
    font-size: var(--h-fs-xl);
    font-weight: 500;
  }

  &__title {
    text-align: center;
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    max-width: 640px;
    margin: 10px auto 0;
  }

  &__table {
    display: flex;
    justify-content: center;
    align-items: stretch;
    margin-top: 45px;
    border-radius: 20px;
    overflow: hidden;
  }

  &__left {
    background: #F7F7F7;
    width: 520px;

  }

  &__left-logo {
    background: #EDEDED;
    height: 86px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img { width: 210px; height: 64px; }
  }

  &__left-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 78px;
    margin: 0 20px;
    color: var(--h-text-black);
    font-size: 20px;
    font-weight: 500;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:first-of-type { margin-top: 0; }
    &:last-of-type { border-bottom: none; }
  }

  &__left-icon { width: 28px; height: 28px; }

  &__pk {
    background: var(--h-primary-light-20);
    width: 160px;
    text-align: center;
  }

  &__pk-badge-wrap {
    background: url('~@/assets/images/home/CoreComparison-pk@2x.png') 100% no-repeat;
    background-size: 100% 100%;
    height: 110px;
    width: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__pk-text {
    color: var(--h-white);
    font-size: 48px;
    font-weight: 700;
    line-height: 1;
  }

  &__pk-label {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--h-primary);
    font-size: 20px;
    font-weight: 500;
    height: 78px;
    margin: 0 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:last-of-type { border-bottom: none; }
  }

  &__right {
    background: rgba(115, 49, 255, 0.05);
    width: 520px;
  }

  &__right-header {
    background: rgba(115, 49, 255, 0.2);
    height: 86px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    img { width: 210px; height: 64px; }
  }

  &__right-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 78px;
    margin: 0 20px;
    color: var(--h-text-black);
    font-size: 20px;
    font-weight: 500;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:last-of-type { border-bottom: none; }
  }

  &__right-icon { width: 28px; height: 28px; }

  /* 移动端双列对比 */
  &__mobile {
    display: none;
  }

  &__mobile-head {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 20px 16px;
    border-radius: 16px 16px 0 0;

    &--left, &--right {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px;
      border-radius: 12px;

      img { height: 28px; object-fit: contain; }
    }

    &--left  { background: #EDEDED; }
    &--right { background: rgba(115, 49, 255, 0.1); }
  }

  &__mobile-vs-badge {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--h-primary);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__mobile-row {
    border-bottom: 1px solid rgba(0,0,0,.06);

    &:last-child { border-bottom: none; }
  }

  &__mobile-label {
    display: block;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: var(--h-primary);
    padding: 14px 16px 8px;
    background: rgba(115, 49, 255, 0.04);
  }

  &__mobile-vs {
    display: flex;
  }

  &__mobile-cell {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 14px;
    font-size: 14px;
    line-height: 1.4;

    &--left  { background: #FAFAFA; }
    &--right { background: rgba(115, 49, 255, 0.03); }
  }

  &__mobile-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
}

/* ====== 6. Zero Code ====== */
.home-zerocode {
  width: var(--h-content);
  margin: 100px auto 0;
  text-align: center;

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    margin: 0;
  }

  &__vs {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 36px;
    height: 400px;
  }

  &__side {
    &--left { text-align: right; margin-right: 40px; }
    &--right { text-align: left; margin-left: 12px; }
  }

  &__num {
    color: var(--h-primary-dark);
    font-weight: 700;

    &--big { font-size: var(--h-fs-stat); line-height: 1; }
    &--small { font-size: var(--h-fs-h3); line-height: 1; }
  }

  &__label {
    display: block;
    color: var(--h-text-gray);
    font-size: var(--h-fs-md);
    margin-top: 8px;
  }

  &__arrow img { width: auto; height: auto; }

  &__checks {
    list-style: none;
    padding: 0;
    color: var(--h-text-dark);
    font-size: var(--h-fs-md);

    li { margin-bottom: 12px; }
  }
}

/* ====== 7. Sell Anywhere ====== */
.home-sell {
  background: rgba(170, 152, 252, 0.4);
  padding: 60px 0 80px;
  margin-top: 100px;

  &__inner {
    width: var(--h-content);
    margin: 0 auto;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    margin: 0;

    &--accent {
      color: var(--h-primary);
      font-size: var(--h-fs-h1);
      font-weight: 600;
    }
  }

  &__desc {
    color: var(--h-text-dark);
    font-size: var(--h-fs-sm);
    line-height: 1.5;
    max-width: 638px;
    margin: 10px 0 0;
  }

  &__btn {
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: #fff;
    cursor: pointer;
    color: #000;
    font-size: var(--h-fs-xl);
    font-weight: 500;
    font-family: var(--h-font);
    border-radius: var(--h-radius-xl);
    padding: 12px 28px;

    img { width: 24px; height: 24px; }
  }

  &__cards {
    display: flex;
    gap: 20px;
    margin-top: 40px;
  }

  &__card {
    background: var(--h-white);
    border-radius: var(--h-radius-md);
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 24px 20px;
    cursor: pointer;
    transition: box-shadow .3s, transform .3s;

    &:hover {
      box-shadow: 0px 8px 30px rgba(115, 49, 255, 0.15);
      transform: translateY(-4px);
    }

    &--reverse {
      flex-direction: column-reverse;

      .home-sell__card-img {
        margin-top: 12px;
      }

      .home-sell__card-row {
        margin-top: 0;
      }
    }
  }

  &__card-img {
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: var(--h-radius-sm);
  }

  &__card-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
  }

  &__card-body {
    flex: 1;
  }

  &__card-title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-xl);
    font-weight: 600;
    margin: 0;
  }

  &__card-desc {
    color: var(--h-text-dark);
    font-size: var(--h-fs-sm);
    line-height: 1.5;
    margin: 12px 0 0;
  }

  &__card-arrow {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    margin-left: 12px;
  }
}

/* ====== 8. Marketing ====== */
.home-marketing {
  background: #F2F2F2;
  padding: 60px 0 80px;
  margin-top: 100px;

  &__inner {
    width: var(--h-content);
    margin: 0 auto;
  }

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    text-align: center;
    margin: 0 0 32px;
  }

  &__tabs {
    display: flex;
    justify-content: center;
    background: var(--h-white);
    border-radius: var(--h-radius-full);
    width: fit-content;
    height: 66px;
    margin: 0 auto;
    padding: 0 4px;
  }

  &__tab {
    border: none;
    background: none;
    cursor: pointer;
    color: var(--h-text-black);
    font-size: var(--h-fs-xl);
    font-weight: 500;
    font-family: var(--h-font);
    padding: 18px 32px;
    white-space: nowrap;
    transition: all .3s;
    border-radius: var(--h-radius-full);

    &--active {
      background: var(--h-primary-light-8);
      color: var(--h-primary);
      margin: 8px 4px;
      padding: 10px 32px;
    }
  }

  &__content {
    margin-top: 60px;
  }

  &__panel {
    display: flex;
    gap: 40px;
    align-items: flex-start;
    justify-content: space-around;
    background: var(--h-white);
    border-radius: 40px;
    padding: 40px;
  }

  &__info {
    flex: 0 0 439px;
  }

  &__info-title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    margin: 0;
  }

  &__info-subtitle {
    color: var(--h-text-dark);
    font-size: var(--h-fs-sm);
    margin: 8px 0 60px;
  }

  &__info-desc {
    color: var(--h-text-dark);
    font-size: var(--h-fs-sm);
    line-height: 1.5;
    margin: 14px 0 0;
  }

  &__visuals {
    display: flex;
    gap: 20px;
    flex-shrink: 0;

    img { border-radius: var(--h-radius-sm); width: auto; height: auto; }
  }
}

/* ====== 9. Trust Metrics ====== */
.home-trust {
  width: var(--h-content);
  margin: 100px auto 0;
  padding: 32px 0 40px;

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    text-align: center;
    margin: 0 0 36px;
  }

  &__stats {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
  }

  &__stat-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
  }

  &__stat-num {
    color: var(--h-primary-dark);
    font-size: var(--h-fs-stat);
    font-weight: 700;
    line-height: 1;
  }

  &__stat-unit {
    color: var(--h-primary-dark);
    font-size: var(--h-fs-h3);
    font-weight: 700;
    margin-top: 0;

    &.is-plus { margin-left: 2px; }
  }

  &__stat-label {
    color: var(--h-text-gray);
    font-size: var(--h-fs-md);
    margin-top: 8px;
  }

  &__dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 26px;
    &--pink { background: #FAA9B3; }
    &--green { background: #A9EAC8; }
    &--purple { background: #D595E9; }
  }
}

/* ====== 10. Target Audience ====== */
.home-audience {
  background: var(--h-bg-gray);
  padding: 60px 0 80px;
  margin-top: 100px;

  &__header { text-align: center; }

  &__label {
    color: var(--h-primary-dark);
    font-size: var(--h-fs-sm);
    font-weight: 500;
  }

  &__title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-h1);
    font-weight: 600;
    margin: 10px 0 0;
  }

  &__cards {
    display: flex;
    gap: 20px;
    width: var(--h-content);
    margin: 40px auto 0;
  }

  &__card {
    background: var(--h-white);
    border-radius: var(--h-radius-md);
    width: 285px;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: box-shadow .3s, transform .3s;

    &:hover {
      box-shadow: 0px 8px 30px rgba(115, 49, 255, 0.15);
      transform: translateY(-4px);
    }
  }

  &__card-avatar {
    width: 245px;
    height: 164px;
    object-fit: cover;
  }

  &__card-title {
    color: var(--h-text-dark);
    font-size: var(--h-fs-xl);
    font-weight: 600;
    margin: 12px 0 0;
  }

  &__card-subtitle {
    color: var(--h-text-dark);
    font-size: var(--h-fs-sm);
    margin: 12px 0 0;
    flex: 1;
  }

  &__card-feature {
    background: var(--h-primary-light-8);
    border-radius: var(--h-radius-sm);
    color: var(--h-purple-tag);
    font-size: var(--h-fs-sm);
    padding: 4px 8px;
    margin-top: 12px;
    text-align: center;
    overflow-wrap: break-word;
  }
}

/* ====== 11. CTA ====== */
.home-cta {
  background: linear-gradient(135deg, #7331FF 0%, #AA98FC 100%);
  padding: 80px 0;
  text-align: center;

  &__inner {
    width: var(--h-content);
    margin: 0 auto;
  }

  &__title {
    color: #fff;
    font-size: var(--h-fs-h1);
    font-weight: 700;
    margin: 0;
  }

  &__desc {
    color: rgba(255, 255, 255, 0.85);
    font-size: var(--h-fs-lg);
    margin: 16px 0 0;
    line-height: 1.6;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 40px;
    border: none;
    cursor: pointer;
    font-family: var(--h-font);
    font-size: var(--h-fs-lg);
    font-weight: 600;
    width: 220px;
    height: 56px;
    background: #fff;
    color: #7331FF;
    border-radius: var(--h-radius-xl);
    box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.2);
    transition: transform .2s, box-shadow .2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0px 12px 32px rgba(0, 0, 0, 0.3);
    }
  }
}

/* ====== Responsive ====== */

/* ---- 1280px: tighten padding ---- */
@media (max-width: 1280px) {
  .home-hero {
    width: 100%;
    &__content { padding-left: 60px; }
  }

  .home-chain {
    &__steps { width: auto; padding: 0 40px; gap: 10px; }
    &__step { width: 160px; }
  }

  .home-sell {
    &__inner { width: auto; padding: 0 20px; }
  }

  .home-marketing {
    &__inner { width: auto; padding: 0 20px; }
  }

  .home-trust {
    width: auto;
    margin: 80px 20px 0;
  }

  .home-audience {
    &__cards { width: auto; padding: 0 20px; flex-wrap: wrap; justify-content: center; }
    &__card { width: 240px; }
  }

  .home-cta {
    &__inner { width: auto; padding: 0 40px; }
  }
}

/* ---- 1024px: tablet ---- */
@media (max-width: 1024px) {
  /* Hero */
  .home-hero {
    height: 600px;
    &__content { padding: 140px 0 0 40px; }
    &__title { font-size: 36px; }
    &__features { font-size: 22px; }
    &__features-desc { max-width: 80%; }
  }

  /* Pain Points */
  .home-painpoints {
    &__cards { width: auto; flex-wrap: wrap; justify-content: center; gap: 16px; padding: 0 20px; }
    &__card { width: 340px; }
  }

  /* Full Chain */
  .home-chain {
    &__steps { flex-wrap: wrap; justify-content: center; gap: 30px; }
    &__step { width: 200px; }
    &__arrow { display: none; }
    &__label { margin-top: 60px; }
  }

  /* Comparison */
  .home-compare {
    width: auto;
    margin: 15px 20px 0;
    &__table { flex-direction: column; align-items: stretch; }
    &__left, &__pk, &__right { width: 100%; }
    &__pk-badge-wrap { width: 100%; }
    &__pk-label { margin: 0 20px; }
  }

  /* Sell */
  .home-sell {
    &__header { flex-direction: column; gap: 20px; }
    &__cards { flex-wrap: wrap; }
    &__card { flex: 0 0 calc(50% - 10px); }
    &__card--reverse { flex-direction: column; }
  }

  /* Marketing */
  .home-marketing {
    &__panel { flex-direction: column; align-items: center; }
    &__info { flex: auto; width: 100%; }
    &__visuals { flex-wrap: wrap; justify-content: center; }
    &__tabs { width: auto; min-width: 420px; }
  }

  /* Trust */
  .home-trust {
    &__stats { flex-wrap: wrap; justify-content: center; gap: 24px; }
    &__stat { flex: 0 0 calc(50% - 24px); }
    &__dot { display: none; }
    &__stat-num { font-size: 42px; }
    &__stat-unit { font-size: 22px; }
  }

  /* Audience */
  .home-audience {
    &__cards { gap: 16px; }
    &__card { width: calc(50% - 8px); }
  }

  /* CTA */
  .home-cta {
    &__title { font-size: 28px; }
  }
}

/* ---- 768px: mobile ---- */
@media (max-width: 768px) {
  /* Hero */
  .home-hero {
    height: auto;
    min-height: 520px;
    margin-top: -56px;
    &__content { padding: 150px 16px 0; text-align: center; }
    &__title { font-size: 28px; }
    &__features { font-size: 18px; letter-spacing: 0; }
    &__features li { overflow-wrap: break-word; }
    &__features-desc { max-width: 100%; font-size: 12px; overflow-wrap: break-word; }
    &__actions { flex-direction: column; align-items: center; }
    &__btn--primary, &__btn--outline { width: 200px; }
  }

  /* Pain Points */
  .home-painpoints {
    &__title { font-size: 24px; }
    &__cards { flex-direction: column; align-items: center; padding: 0 16px; }
    &__card { width: 100%; max-width: 400px; }
    &__card-title { overflow-wrap: break-word; }
    &__card-desc { overflow-wrap: break-word; }
    &__quote { margin: 40px auto 60px; flex-direction: column; text-align: center; }
    &__quote span { font-size: 18px; overflow-wrap: break-word; }
  }

  /* Full Chain */
  .home-chain {
    &__title { font-size: 24px; }
    &__desc { font-size: 14px; padding: 0 16px; overflow-wrap: break-word; }
    &__steps { flex-direction: column; align-items: center; gap: 32px; padding: 0 16px; }
    &__step { width: 100%; max-width: 320px; }
    &__step-desc { font-size: 13px; overflow-wrap: break-word; }
    &__label { margin-top: 40px; }
  }

  /* Comparison */
  .home-compare {
    margin: 15px 16px 0;
    &__title { font-size: 24px; max-width: 100%; overflow-wrap: break-word; }
    &__table { display: none; }
    &__mobile { display: block; }
  }

  /* Sell */
  .home-sell {
    padding: 40px 0 60px;
    margin-top: 60px;
    &__inner { padding: 0 16px; }
    &__title { font-size: 24px; }
    &__title--accent { font-size: 24px; }
    &__desc { font-size: 13px; overflow-wrap: break-word; }
    &__btn { align-self: flex-start; }
    &__cards { flex-direction: column; gap: 16px; }
    &__card { flex: auto; max-width: 100%; }
    &__card--reverse { flex-direction: column; }
    &__card-title { font-size: 18px; overflow-wrap: break-word; }
    &__card-desc { font-size: 13px; overflow-wrap: break-word; }
    &__card-arrow { width: 32px; height: 32px; }
  }

  /* Marketing */
  .home-marketing {
    padding: 40px 0 60px;
    margin-top: 60px;
    &__inner { padding: 0 16px; }
    &__title { font-size: 24px; margin-bottom: 24px; }
    &__tabs { width: 100%; min-width: auto; height: auto; flex-wrap: wrap; border-radius: 12px; padding: 4px; }
    &__tab { font-size: 14px; padding: 10px 16px; white-space: normal; }
    &__tab--active { padding: 10px 16px; margin: 0; }
    &__panel { border-radius: 20px; padding: 24px 16px; }
    &__info-title { font-size: 24px; }
    &__info-subtitle { margin-bottom: 30px; }
    &__info-desc { overflow-wrap: break-word; }
    &__visuals img { width: 100%; height: auto; }
  }

  /* Trust */
  .home-trust {
    margin: 60px 16px 0;
    padding: 24px 0 32px;
    &__title { font-size: 24px; overflow-wrap: break-word; }
    &__stats { flex-wrap: wrap; justify-content: center; gap: 32px; }
    &__stat { flex: 0 0 calc(50% - 16px); }
    &__stat-num { font-size: 36px; }
    &__stat-unit { font-size: 20px; }
    &__stat-label { font-size: 14px; overflow-wrap: break-word; }
  }

  /* Audience */
  .home-audience {
    padding: 40px 0 60px;
    margin-top: 60px;
    &__title { font-size: 24px; overflow-wrap: break-word; }
    &__cards { padding: 0 16px; flex-direction: column; align-items: center; }
    &__card { width: 100%; max-width: 360px; text-align: center; align-items: center; }
    &__card-avatar { width: 200px; height: 134px; }
    &__card-title { font-size: 18px; }
    &__card-subtitle { font-size: 13px; overflow-wrap: break-word; }
    &__card-feature { font-size: 13px; white-space: normal; }
  }

  /* CTA */
  .home-cta {
    padding: 60px 0;
    &__inner { padding: 0 16px; }
    &__title { font-size: 24px; }
    &__desc { font-size: 14px; }
    &__btn { width: 200px; height: 48px; font-size: 16px; }
  }
}

</style>
